<script lang="ts" setup>
import { StepsController } from '@/controllers/StepsController'
import formStore from '@/store/FormStore'
import { CharacteristicType, ProductType, RecommendationBadgeType } from '@/helper/Types'
import { validateComponent, ValidatorType } from '@/helper/ValidateDirective'
import projectStore from '@/store/ProjectStore'
import { computed, ref, watch } from 'vue'
import { formatNumber } from '@/helper/Translations'
import { DhwStorage } from '@/services/models/implementations/DhwStorage'
import ProductsSelectTable, { ProductColumn } from '@/components/ProductsSelectTable.vue'
import SectionHeadline from '@/components/SectionHeadline.vue'
import ExistingDhwTank from '@/components/steps/components/ExistingDhwTank.vue'
import ExclusionHint from '@/components/steps/components/ExclusionHint.vue'
import userStore from '@/store/UserStore'
import ProductAvailabilityLink from '@/components/ProductAvailabilityLink.vue'
import { ProductResponse } from '@/services/models/ProductResponse.ts'

const props = defineProps<{
  validator: ValidatorType
  showExistingDhwCheckbox: boolean
}>()

const dhwStorageColumns: ProductColumn[] = [
  {
    columnName: 'step_components.table_product',
    cssClass: 'is--product',
    valCb: (val: ProductResponse) => val.name
  },
  {
    columnName: 'step_components.table_storage_volume',
    cssClass: 'is--volume',
    valCb: (val: ProductResponse) => formatNumber((val as DhwStorage).chr?.volume) + ' l'
  }
]

const dhwStorages = computed(() => projectStore.dhwStorages)

const showExistingDhwTankCheckbox = computed(() => projectStore.projectProperties.hybrid.val && props.showExistingDhwCheckbox && !combiBoilerSelected.value)
const showExistingDhwTankForm = ref(projectStore.projectProperties.existingDhwTank.val !== undefined)
const showSection = computed(() => projectStore.projectProperties.dhwEnabled.val && !projectStore.selectedHeatpump.getProductCharacteristic(CharacteristicType.DHW_STORAGE))

const combiBoilerSelected = computed(() => {
  const backupHeater = projectStore.backupHeaters
    .find(buh => buh.id === formStore.data.backupHeaterId)

  return backupHeater?.chr?.dhwCapable
    || formStore.data.existingBackupHeater?.dhw
    || false
})

const hasIndoorUnitWithDhwTank = computed((): boolean => {
  const product = projectStore.getSelectedProduct(ProductType.ADDITIONAL_PRODUCT)
  return projectStore.getProductCharacteristic(product, CharacteristicType.DHW_STORAGE) !== undefined
})

function resetInput() {
  formStore.data.dhwStorageId = ''
  formStore.data.existingDhwTank = {}
}

watch(() => showSection.value, value => {
  if (!value) {
    resetInput()
    showExistingDhwTankForm.value = false
  }
})

watch(() => combiBoilerSelected.value, value => {
  if (value) {
    resetInput()
  }
})

watch(() => showExistingDhwTankForm.value, () => {
  resetInput()
  StepsController.currentStepValid(false)
})

watch(() => showExistingDhwTankCheckbox.value, value => {
  if (!value) {
    formStore.data.existingDhwTank = {}
    showExistingDhwTankForm.value = false
  }
})

watch(() => dhwStorages.value, value => {
  const selectedStorage = value.find(storage => storage.inSalePackage === true)
  if (selectedStorage) {
    formStore.data.dhwStorageId = selectedStorage.id
  }
}, { immediate: true })

function validate(): boolean {
  if (!showSection.value || combiBoilerSelected.value) {
    return true
  }

  if (showExistingDhwTankForm.value) {
    return !!formStore.data.existingDhwTank?.name && validateComponent(props.validator)
  }

  return !!formStore.data.dhwStorageId || hasIndoorUnitWithDhwTank.value
}

defineExpose({ validate })
</script>

<template>
  <template v-if="showSection">
    <form id="dhw-storage-form" class="steps__form steps-table" @submit.prevent="">
      <div class="steps-table__header">
        <SectionHeadline class="steps-table__title fTable__title" translation-key="step_components.title_dhw_storage" />

        <label v-if="showExistingDhwTankCheckbox" class="form__checkbox steps-table__toggle">
          {{ $t('step_components.use_existing_dhw_storage_tank') }}
          <input id="cb-use-existing-dhw-tank" v-model="showExistingDhwTankForm" type="checkbox">
          <span class="form__cm is--large" />
        </label>

        <ProductAvailabilityLink :availability-link="userStore.productAvailabilityLinks?.dhwTanks" />
      </div>

      <template v-if="!combiBoilerSelected">
        <div v-if="!projectStore.isDhwTankSelected()">
          <ExistingDhwTank
            v-if="showExistingDhwTankForm"
            :validator="validator"
            @input="StepsController.currentStepValid(false)"
            @select-button-clicked="showExistingDhwTankForm = false"
          />
          <ProductsSelectTable
            v-else-if="dhwStorages.length > 0"
            v-model="formStore.data.dhwStorageId"
            :columns="dhwStorageColumns"
            :products="dhwStorages"
            :recommendation-type="RecommendationBadgeType.LEFT"
            :unselectable="false"
            product-name="dhw-storage"
          />

          <ExclusionHint
            v-else
            :text="$t('step_components.no_dhw_storages_available_text')"
            :title="$t('step_components.no_dhw_storages_available')"
          />
        </div>

        <ExclusionHint
          v-else
          :text="$t('step_components.dhw_storage_already_included_text')"
          :title="$t('step_components.dhw_storage_already_included')"
        />
      </template>

      <ExclusionHint
        v-else
        :text="$t('step_components.combi_boiler_included_text')"
        :title="$t('step_components.combi_boiler_included')"
      />
    </form>
  </template>
</template>
